import React from 'react';
import { useNavigate } from 'react-router-dom';

import PageLayout from '@/components/layout/PageLayout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { Selector } from '@/components/ui/inputs/selector';
import Table from '@/components/ui/tables/Table';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useScheduleList } from '@/hooks/schedule/useScheduleList';
import { PermissionGuard } from '@/components/layout';


export const ScheduleList: React.FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onEnterSearch,
    onSortChange,
    isLoading,
    isExporting,
    onExport,
    filters,
    applyFilters,
    timezoneOptions,
  } = useScheduleList();

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="schedule-list-page">
        {/* Header actions */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            <PermissionGuard action="create">
              <PrimaryButton
                iconLeft={<IconScout name="plus" />}
                onClick={() => navigate(ROUTE_DEFINITIONS.HORARIO_CREAR.path)}
              >
                {translate('schedule:actions.newSchedule')}
              </PrimaryButton>
            </PermissionGuard>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <ExportDropdown
              onExport={onExport}
              placement="right"
              isLoading={isExporting}
              availableFormats={['EXCEL']}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <FilterDropdown
            extraFilters={
              <div className="flex flex-col gap-3 pt-2">
                <Selector
                  label={translate('schedule:filters.timezone')}
                  options={[
                    { value: 'all', label: translate('schedule:filters.all') },
                    ...timezoneOptions,
                  ]}
                  value={filters.timeZoneId !== null ? String(filters.timeZoneId) : 'all'}
                  onChange={(value) => {
                    applyFilters((previous) => ({
                      ...previous,
                      timeZoneId: value === 'all' ? null : Number(value),
                    }));
                  }}
                />
                <Selector
                  label={translate('schedule:filters.isTwoDays')}
                  options={[
                    { value: 'all', label: translate('schedule:filters.all') },
                    { value: 'true', label: translate('schedule:filters.yes') },
                    { value: 'false', label: translate('schedule:filters.no') },
                  ]}
                  value={
                    filters.isTwoDaySchedule === null
                      ? 'all'
                      : String(filters.isTwoDaySchedule)
                  }
                  onChange={(value) => {
                    applyFilters((previous) => ({
                      ...previous,
                      isTwoDaySchedule: value === 'all' ? null : value === 'true',
                    }));
                  }}
                />
              </div>
            }
          />
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          searchable
          onSearch={onSearch}
          onEnterSearch={onEnterSearch}
          serverSideSort
          onSortChange={onSortChange}
          initialSort={{ key: 'name', direction: 'asc' }}
          actions={actions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="schedule-table"
        />
      </div>
    </PageLayout>
  );
};

export default ScheduleList;
